import { toast } from '../lib/toastBus';

export const TOKEN_KEY = 'hbc_token';

/** Fejlformatet fra shared/errors.py: {error: {code, message_da, details}}. */
export interface ApiErrorBody {
  error?: {
    code?: string;
    message_da?: string;
    details?: Record<string, unknown>;
  };
  /**
   * FastAPI's eget format. Routing bruger det med en dansk tekst
   * (kontrakt 0.4.2), saa den vises ogsaa som messageDa.
   */
  detail?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Dansk tekst, der kan vises direkte til brugeren. */
  readonly messageDa: string;
  readonly details: Record<string, unknown>;

  constructor(
    status: number,
    code: string,
    messageDa: string,
    details: Record<string, unknown> = {},
  ) {
    super(`${status} ${code}: ${messageDa}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.messageDa = messageDa;
    this.details = details;
  }
}

/** Fallback-tekster, naar serveren ikke sender message_da. */
function defaultMessage(status: number): string {
  if (status === 0) return 'Kunne ikke få forbindelse til serveren.';
  if (status === 400) return 'Forespørgslen kunne ikke behandles.';
  if (status === 401) return 'Din session er udløbet. Log ind igen.';
  if (status === 403) return 'Du har ikke adgang til dette.';
  if (status === 404) return 'Ressourcen blev ikke fundet.';
  if (status === 409) return 'Handlingen er i konflikt med eksisterende data.';
  if (status === 422) return 'Nogle felter er ikke udfyldt korrekt.';
  if (status >= 500) return 'Der opstod en serverfejl. Prøv igen.';
  return 'Der opstod en uventet fejl.';
}

/* -------------------------------------------------------------- token-styring */

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* privat browsertilstand: vi kører videre uden persistering */
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignoreres */
  }
}

let unauthorizedHandler: (() => void) | null = null;

/** AuthProvider registrerer her, hvad der skal ske ved 401 (log ud + redirect). */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/* -------------------------------------------------------------- kald */

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Send ikke Authorization-header (bruges af login). */
  anonymous?: boolean;
  /** Undlad automatisk fejl-toast; kalderen viser selv fejlen. */
  silent?: boolean;
  /**
   * Et 401 betyder her ikke "sessionen er udløbet": token og unauthorized-handler
   * røres ikke (Microsoft-loginets token-kald sker uden for en session).
   */
  skipUnauthorizedHandler?: boolean;
  signal?: AbortSignal;
  /** "blob": returner svaret som Blob (filer, der kraever JWT). */
  responseType?: 'json' | 'blob';
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = path.startsWith('/api') ? path : `/api${path.startsWith('/') ? '' : '/'}${path}`;
  if (!query) return base;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${base}?${qs}` : base;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    query,
    anonymous,
    silent,
    skipUnauthorizedHandler,
    signal,
    responseType,
  } = options;

  // FormData (multipart) saettes af browseren inkl. boundary; JSON for alt andet.
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  const headers: Record<string, string> = { Accept: responseType === 'blob' ? '*/*' : 'application/json' };
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (!anonymous) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
      signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    const error = new ApiError(0, 'network_error', defaultMessage(0));
    if (!silent) toast.error(error.messageDa);
    throw error;
  }

  if (responseType === 'blob' && response.ok) return (await response.blob()) as T;

  const payload = await parseBody(response);

  if (!response.ok) {
    const envelope = (payload ?? {}) as ApiErrorBody;
    const inner = typeof envelope === 'object' && envelope !== null ? envelope.error : undefined;
    const detail =
      typeof envelope === 'object' && envelope !== null && typeof envelope.detail === 'string'
        ? envelope.detail
        : undefined;
    const error = new ApiError(
      response.status,
      inner?.code ?? `http_${response.status}`,
      inner?.message_da ?? detail ?? defaultMessage(response.status),
      inner?.details ?? {},
    );

    if (response.status === 401 && !skipUnauthorizedHandler) {
      clearToken();
      unauthorizedHandler?.();
    }
    if (!silent) toast.error(error.messageDa);
    throw error;
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
